import LoadError from "@/components/LoadError";
import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronLeft, Rocket } from "lucide-react";
import { apiFetch } from "@/lib/api";
import { Card, EmptyState, Pill, Spinner } from "@/components/earnos/kit";
import { colors, money } from "@/lib/theme";

function Stat({ label, value }) {
  return (
    <div className="rounded-xl border border-line bg-surface-2 p-4">
      <p className="text-xl font-extrabold text-ink">{value}</p>
      <p className="text-sm text-ink-2 mt-0.5">{label}</p>
    </div>
  );
}

export default function Portfolio() {
  const navigate = useNavigate();
  const [p, setP] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");

  const load = useCallback(async () => {
    setLoading(true); setLoadError("");
    try { setP(await apiFetch("/portfolio")); } catch (e) { setLoadError(e.message || "Service unavailable. Please retry."); } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading) return <Spinner className="py-24" />;

  const engines = [...(p?.active_engines || []).map((e) => ({ ...e, _s: "Active" })), ...(p?.paused_engines || []).map((e) => ({ ...e, _s: "Paused" }))];

  if (loadError && !loading) return <LoadError title="Income portfolio" message={loadError} onRetry={load} />;

  return (
    <div className="animate-fade-in max-w-3xl">
      <div className="flex items-center gap-3 mb-4">
        <button onClick={() => navigate(-1)} data-testid="portfolio-back" className="text-ink lg:hidden"><ChevronLeft size={26} /></button>
        <h1 className="text-2xl font-extrabold tracking-tight text-ink">Income Portfolio</h1>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Stat label="Revenue received" value={money(p?.total_revenue_received || 0)} />
        <Stat label="Net estimated" value={money(p?.net_estimated || 0)} />
        <Stat label="Active engines" value={String((p?.active_engines || []).length)} />
        <Stat label="Income assets" value={String(p?.asset_count || 0)} />
      </div>

      <h3 className="text-lg font-bold text-ink mt-8">Your engines</h3>
      {engines.length === 0 ? (
        <EmptyState icon={Rocket} title="No engines yet" message="Create an Income Engine to start your portfolio." actionTitle="Go to Engines" onAction={() => navigate("/engine")} />
      ) : (
        <div className="space-y-3 mt-3">
          {engines.map((e) => (
            <Card key={e.engine_id}>
              <div className="flex items-center justify-between gap-2">
                <p className="text-lg font-bold text-ink flex-1">{e.path_name}</p>
                <Pill label={e._s} color={e._s === "Active" ? colors.success : colors.onSurfaceSecondary} />
              </div>
              <p className="text-base text-ink-2 mt-1">{e.potential_revenue || "—"}</p>
              {e.is_demo ? <div className="mt-2"><Pill label="Demo" color={colors.warning} /></div> : null}
            </Card>
          ))}
        </div>
      )}
      {p?.note ? <p className="text-sm text-ink-2 mt-8 leading-relaxed">{p.note}</p> : null}
    </div>
  );
}
